import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { RootState } from 'store'
import { storeCollabRemovalRequest } from 'services/api/collaboration'
import { getErrorMessage } from 'utils/helpers'
import inModal from 'utils/hoc/inModal'
import FileInput from 'Components/collaboration/milestone/form/FileInput'

const IMAGE_ACCEPT = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
}

interface RequestCollabDeletionProps {
    isActive: boolean
    onClose: () => void
    onSuccess: () => void
}

const RequestCollabDeletion: React.FC<RequestCollabDeletionProps> = ({ onClose, onSuccess }) => {
    const { campaignId, collaborationId } = useParams()
    const [reason, setReason] = useState('')
    const [file, setFile] = useState<File | null>(null)
    const [errors, setErrors] = useState<{ reason?: string; file?: string }>({})
    const [submitting, setSubmitting] = useState(false)

    const validate = () => {
        const nextErrors: { reason?: string; file?: string } = {}
        if (!reason.trim()) {
            nextErrors.reason = 'Reason is required.'
        }
        if (file && !file.type.startsWith('image/')) {
            nextErrors.file = 'Please upload an image.'
        }
        setErrors(nextErrors)
        return Object.keys(nextErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validate() || !campaignId || !collaborationId) return

        try {
            setSubmitting(true)
            await storeCollabRemovalRequest(campaignId, collaborationId, {
                reason: reason.trim(),
                file: file ?? undefined,
            })
            toast.success('Collab deletion request submitted.')
            onSuccess()
            onClose()
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to submit deletion request.'))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="form-builder-form">
            <div className="form-builder-field">
                <label className="form-builder-label" htmlFor="collab-deletion-reason">
                    Reason <span className="text-danger">*</span>
                </label>
                <textarea
                    id="collab-deletion-reason"
                    rows={4}
                    className="form-control form-builder-textarea"
                    value={reason}
                    onChange={(e) => {
                        setReason(e.target.value)
                        if (errors.reason) setErrors((prev) => ({ ...prev, reason: undefined }))
                    }}
                    placeholder="Why should this collab be deleted?"
                />
                {errors.reason && <span className="text-danger">{errors.reason}</span>}
            </div>
            <div className="form-builder-field">
                <label className="form-builder-label">
                    Image
                </label>
                <FileInput
                    accept={IMAGE_ACCEPT}
                    onChange={(selectedFile) => {
                        const nextFile = selectedFile instanceof File ? selectedFile : null
                        setFile(nextFile)
                        if (errors.file) setErrors((prev) => ({ ...prev, file: undefined }))
                    }}
                />
                {errors.file && <span className="text-danger">{errors.file}</span>}
            </div>
            <div className="milestone-step-actions">
                <button
                    type="button"
                    className="theme-btn theme-btn-primary-outlined"
                    onClick={onClose}
                    disabled={submitting}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="theme-btn theme-btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? 'Submitting...' : 'Submit'}
                </button>
            </div>
        </div>
    )
}

const RequestCollabDeletionModal: React.FC<RequestCollabDeletionProps> = (props) => {
    const ModalComponent = useMemo(
        () => inModal(RequestCollabDeletion, 'Request Collab Deletion'),
        []
    )
    return <ModalComponent {...props} />
}

export default RequestCollabDeletionModal
